import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAuth } from './authStore';
import { consentService } from '../firebase/services/consentService';

export interface ConsentPreferences {
  necessary: boolean; // Always true, required for basic functionality
  analytics: boolean; // Google Analytics
  advertising: boolean; // AdSense and advertising cookies
  functional: boolean; // Enhanced user experience features
  lastUpdated: Date;
}

export interface GDPRConsent {
  hasConsented: boolean;
  preferences: ConsentPreferences;
  isEUUser: boolean;
  consentDate: Date | null;
  consentVersion: string;
}

const CONSENT_VERSION = '1.0';
const CONSENT_STORAGE_KEY = 'gdpr_consent';

export const useConsentStore = defineStore('consent', () => {
  const consent = ref<GDPRConsent>({
    hasConsented: false,
    preferences: {
      necessary: true,
      analytics: false,
      advertising: false,
      functional: false,
      lastUpdated: new Date()
    },
    isEUUser: false,
    consentDate: null,
    consentVersion: CONSENT_VERSION
  });

  const showConsentBanner = ref(false);
  const isLoading = ref(false);

  // Computed properties
  const canUseAnalytics = computed(() => 
    consent.value.hasConsented && consent.value.preferences.analytics
  );

  const canUseAdvertising = computed(() => 
    consent.value.hasConsented && consent.value.preferences.advertising
  );

  const canUseFunctional = computed(() => 
    consent.value.hasConsented && consent.value.preferences.functional
  );

  const needsConsent = computed(() => 
    consent.value.isEUUser && (!consent.value.hasConsented || 
    consent.value.consentVersion !== CONSENT_VERSION)
  );

  // Check if user is in EU based on geolocation
  const checkEULocation = async (): Promise<boolean> => {
    try {
      // For testing purposes, always return true to show the banner
      return true;
      
      // In production, use the geolocation service
      // const { geoLocationService } = await import('../services/geoLocationService');
      // return await geoLocationService.isEUUser();
    } catch (error) {
      console.warn('Could not determine user location for GDPR compliance:', error);
      // Default to showing consent for safety
      return true;
    }
  };

  // Load consent from localStorage
  const loadConsent = () => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored) {
        const parsedConsent = JSON.parse(stored);
        
        // Convert date strings back to Date objects
        if (parsedConsent.consentDate) {
          parsedConsent.consentDate = new Date(parsedConsent.consentDate);
        }
        if (parsedConsent.preferences?.lastUpdated) {
          parsedConsent.preferences.lastUpdated = new Date(parsedConsent.preferences.lastUpdated);
        }
        
        consent.value = { ...consent.value, ...parsedConsent };
      }
    } catch (error) {
      console.error('Error loading consent preferences:', error);
    }
  };

  // Save consent to localStorage
  const saveConsent = () => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent.value));
    } catch (error) {
      console.error('Error saving consent preferences:', error);
    }
  };

  // Initialize GDPR compliance check
  const initializeGDPR = async () => {
    isLoading.value = true;
    
    try {
      // Load existing consent from localStorage
      loadConsent();
      
      // Check if user is logged in
      const authStore = useAuth();
      const userId = authStore.user?.uid;
      
      // If user is logged in, try to get their consent preferences from Firebase
      if (userId) {
        const userConsent = await consentService.getUserConsent(userId);
        if (userConsent) {
          // Update local consent with user's stored preferences
          consent.value = {
            ...consent.value,
            hasConsented: userConsent.hasConsented,
            consentDate: userConsent.consentDate,
            consentVersion: userConsent.consentVersion,
            preferences: {
              ...consent.value.preferences,
              analytics: userConsent.analytics,
              advertising: userConsent.advertising,
              functional: userConsent.functional,
              lastUpdated: new Date()
            }
          };
        }
      }
      
      // Check if user is in EU
      const isEU = await checkEULocation();
      consent.value.isEUUser = isEU;
      
      // Show banner if consent is needed
      if (needsConsent.value) {
        showConsentBanner.value = true;
      }
      
      // Save updated consent data
      saveConsent();
      
      // Initialize services based on current consent
      initializeThirdPartyServices();
    } catch (error) {
      console.error('Error initializing GDPR compliance:', error);
      // Show consent banner as fallback
      showConsentBanner.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  // Accept all cookies
  const acceptAll = async () => {
    try {
      isLoading.value = true;
      
      // Update local state
      consent.value = {
        hasConsented: true,
        preferences: {
          necessary: true,
          analytics: true,
          advertising: true,
          functional: true,
          lastUpdated: new Date()
        },
        isEUUser: consent.value.isEUUser,
        consentDate: new Date(),
        consentVersion: CONSENT_VERSION
      };
      
      // Save to localStorage
      saveConsent();
      
      // Save to Firebase if user is logged in
      const authStore = useAuth();
      const userId = authStore.user?.uid;
      if (userId) {
        try {
          await consentService.saveUserConsent(userId, {
            hasConsented: true,
            consentDate: new Date(),
            consentVersion: CONSENT_VERSION,
            analytics: true,
            advertising: true,
            functional: true
          });
          
          // Also update the adConsent field for backward compatibility
          await consentService.updateAdConsent(userId, true);
        } catch (error) {
          console.error('Error saving consent to Firebase:', error);
        }
      }
      
      // Hide the banner after a short delay for smooth transition
      setTimeout(() => {
        showConsentBanner.value = false;
      }, 300);
      
      // Trigger analytics and advertising initialization
      initializeThirdPartyServices();
    } catch (error) {
      console.error('Error accepting all cookies:', error);
    } finally {
      isLoading.value = false;
    }
  };

  // Accept only necessary cookies
  const acceptNecessary = async () => {
    consent.value = {
      hasConsented: true,
      preferences: {
        necessary: true,
        analytics: false,
        advertising: false,
        functional: false,
        lastUpdated: new Date()
      },
      isEUUser: consent.value.isEUUser,
      consentDate: new Date(),
      consentVersion: CONSENT_VERSION
    };
    
    showConsentBanner.value = false;
    saveConsent();
    
    // Save to Firebase if user is logged in
    const authStore = useAuth();
    const userId = authStore.user?.uid;
    if (userId) {
      try {
        await consentService.saveUserConsent(userId, {
          hasConsented: true,
          consentDate: new Date(),
          consentVersion: CONSENT_VERSION,
          analytics: false,
          advertising: false,
          functional: false
        });
        
        // Also update the adConsent field for backward compatibility
        await consentService.updateAdConsent(userId, false);
      } catch (error) {
        console.error('Error saving consent to Firebase:', error);
      }
    }
  };

  // Update specific consent preferences
  const updatePreferences = async (preferences: Partial<ConsentPreferences>) => {
    consent.value.preferences = {
      ...consent.value.preferences,
      ...preferences,
      necessary: true, // Always true
      lastUpdated: new Date()
    };
    
    consent.value.consentDate = new Date();
    saveConsent();
    
    // Save to Firebase if user is logged in
    const authStore = useAuth();
    const userId = authStore.user?.uid;
    if (userId) {
      try {
        await consentService.saveUserConsent(userId, {
          hasConsented: true,
          consentDate: new Date(),
          consentVersion: CONSENT_VERSION,
          analytics: consent.value.preferences.analytics,
          advertising: consent.value.preferences.advertising,
          functional: consent.value.preferences.functional
        });
        
        // Also update the adConsent field for backward compatibility
        await consentService.updateAdConsent(userId, consent.value.preferences.advertising);
      } catch (error) {
        console.error('Error saving consent to Firebase:', error);
      }
    }
    
    // Re-initialize services based on new preferences
    initializeThirdPartyServices();
  };

  // Withdraw consent (opt-out)
  const withdrawConsent = async () => {
    consent.value = {
      hasConsented: true,
      preferences: {
        necessary: true,
        analytics: false,
        advertising: false,
        functional: false,
        lastUpdated: new Date()
      },
      isEUUser: consent.value.isEUUser,
      consentDate: new Date(),
      consentVersion: CONSENT_VERSION
    };
    
    saveConsent();
    
    // Save to Firebase if user is logged in
    const authStore = useAuth();
    const userId = authStore.user?.uid;
    if (userId) {
      try {
        await consentService.saveUserConsent(userId, {
          hasConsented: true,
          consentDate: new Date(),
          consentVersion: CONSENT_VERSION,
          analytics: false,
          advertising: false,
          functional: false
        });
        
        // Also update the adConsent field for backward compatibility
        await consentService.updateAdConsent(userId, false);
      } catch (error) {
        console.error('Error saving consent withdrawal to Firebase:', error);
      }
    }
    
    // Clean up third-party services
    cleanupThirdPartyServices();
  };

  // Initialize third-party services based on consent
  const initializeThirdPartyServices = () => {
    // Initialize Google Analytics if consented
    if (canUseAnalytics.value) {
      // Initialize GA4 here
      try {
        // Lazy-import analytics service to avoid loading on serverside or when not needed
        import('../firebase/services/analyticsService').then(({ initializeAnalytics, enableAnalyticsCollection }) => {
          // Import firebase app from config
          import('../firebase/config').then(({ default: firebaseApp }) => {
            initializeAnalytics(firebaseApp).then(() => {
              enableAnalyticsCollection(true);
            }).catch(err => console.warn('Analytics init error:', err));
          }).catch(err => console.warn('Could not import firebase app for analytics:', err));
        }).catch(err => console.warn('Could not load analytics service:', err));
      } catch (error) {
        console.warn('Error initializing analytics:', error);
      }
    } else {
      // Ensure analytics collection is disabled if no consent
      try {
        import('../firebase/services/analyticsService').then(({ enableAnalyticsCollection }) => {
          enableAnalyticsCollection(false);
        }).catch(() => {
          // ignore
        });
      } catch (error) {
        // ignore
      }
    }
    
    // Initialize AdSense/AdMob if consented
    if (canUseAdvertising.value) {
      // Initialize AdSense for web or AdMob for mobile
      console.log('Initializing advertising services');
      
      // Import AdMob service dynamically to avoid issues on web
      import('../services/admobService').then(({ admobService }) => {
        if (admobService.isEnabled()) {
          admobService.initialize();
        }
      }).catch(error => {
        console.warn('AdMob service not available:', error);
      });
    }
  };

  // Clean up third-party services
  const cleanupThirdPartyServices = () => {
    // Clean up analytics cookies and tracking
    console.log('Cleaning up third-party services');
    
    // Remove Google Analytics cookies
    const gaCookies = ['_ga', '_ga_*', '_gid', '_gat', '_gtag_*'];
    gaCookies.forEach(cookieName => {
      if (cookieName.includes('*')) {
        // Handle wildcard cookies
        const prefix = cookieName.replace('*', '');
        document.cookie.split(';').forEach(cookie => {
          const name = cookie.split('=')[0].trim();
          if (name.startsWith(prefix)) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          }
        });
      } else {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });

    // Also call analytics service to perform additional cleanup if available
    try {
      import('../firebase/services/analyticsService').then(({ disableAndClearAnalytics }) => {
        disableAndClearAnalytics();
      }).catch(() => {
        // ignore
      });
    } catch (error) {
      // ignore
    }
  };

  return {
    consent,
    showConsentBanner,
    isLoading,
    canUseAnalytics,
    canUseAdvertising,
    canUseFunctional,
    needsConsent,
    initializeGDPR,
    acceptAll,
    acceptNecessary,
    updatePreferences,
    withdrawConsent,
    loadConsent,
    saveConsent
  };
});